import {
  ChamberMemberProAccountAllocationTypeEnum,
  ChamberMemberProEmailBodyFormatEnum,
  ChamberMemberProEmailRecipientRoutingModeEnum,
  ChamberMemberProEventTicketCreditCardPaymentModeEnum,
  ChamberMemberProInvoicePaymentTermsEnum,
  ChamberMemberProManualPaymentMethodTypeEnum,
  ChamberMemberProSenderDomainDnsRecordTypeEnum,
  ChamberMemberProSenderDomainStatusEnum,
  ChamberMemberProStripeConnectModeEnum,
  ChamberMemberProStripeConnectStatusEnum,
} from "../../../../graphql/types";

export type MembershipPricingModel = "fixed" | "employeeRange";

export type MembershipModelPaymentResponsibility =
  | "Member/applicant pays"
  | "Third party pays the chamber"
  | "No charge";

export type PaymentSchedule = "annual" | "monthly";

export type EnabledPaymentSchedules = Record<PaymentSchedule, boolean>;

// Chamber Set Up > Membership Settings Step 6 "Membership Status" (CMP
// Dashboard (20) L8624-8651): option 1 = allow payment before approval,
// option 2 = collect payment after approval, option 3 = card payment
// required to submit.
export type PaymentBeforeApprovalRule =
  | "Allow payment before approval"
  | "Collect payment after approval"
  | "Card payment required to submit";

export type MemberBenefit = {
  id: string;
  label: string;
};

export type MembershipAdditionalCost = {
  id: string;
  code: string;
  name: string;
  enabled: boolean;
  annualAmount: string;
  monthlyAmount: string;
  accountNumber: string;
  description: string;
  // Design (11): an add-on is a fixed amount or a percentage of the tier fee.
  // Optional so existing literals stay valid; the mapper always sets them.
  feeType?: MembershipAdditionalCostFeeType;
  percentage?: string;
  // Moeen (Oct 2 2026): no tax on this add-on (for example a voluntary fund).
  // Optional so existing literals stay valid; the mapper always sets it.
  taxExempt?: boolean;
};

export type MembershipAdditionalCostFeeType = "fixed" | "percentage";

export type MembershipEmployeeRange = {
  id: string;
  code: string;
  from: string;
  to: string;
  annualPrice: string;
  monthlyPrice: string;
  // D-CS8: stored and returned only. 0 shows as "00" like the tier target.
  // Optional so existing range literals stay valid; the mapper always sets it.
  memberTarget?: number;
};

export type MembershipModel = {
  id: string;
  code: string;
  name: string;
  description: string;
  memberTarget: number;
  enabled: boolean;
  publicMembershipApplicationEnabled: boolean;
  position: number;
  paymentResponsibility: MembershipModelPaymentResponsibility;
  thirdPartyPayerName: string;
  thirdPartyPaymentNotes: string;
  pricingModel: MembershipPricingModel;
  annualPrice: string;
  monthlyPrice: string;
  includedBenefitIds: string[];
  additionalCosts: MembershipAdditionalCost[];
  ranges: MembershipEmployeeRange[];
  // Design (11) tier icon (hosted URL, or a data URL until saved) and
  // employee eligibility. Optional so existing literals stay valid.
  iconUrl?: string;
  eligibilityEmployeesFrom?: string;
  eligibilityEmployeesTo?: string;
};

export type PaymentMethod = {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  emailSubject: string;
  emailBody: string;
};

export type StripeConnectSettings = {
  mode: ChamberMemberProStripeConnectModeEnum;
  status: ChamberMemberProStripeConnectStatusEnum;
  connectedAccountId?: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  creditCardPaymentsEnabled: boolean;
  creditCardPaymentsReady?: boolean | null;
  onboardingStartedAtUtcMilli?: number | null;
  connectedAtUtcMilli?: number | null;
  lastStatusCheckedAtUtcMilli?: number | null;
};

export type CardProcessingFeeSettings = {
  enabled: boolean;
  percentage: string;
  fixedAmount: string;
};

export type ProvincialChamberFeeSettings = {
  enabled: boolean;
  amount: string;
  showSeparateOnInvoice: boolean;
};

export type InvoiceTaxCategorySettings = {
  enabled: boolean;
  firstTaxRateLabel: string;
  firstTaxRatePercentage: string;
  secondTaxRateLabel: string;
  secondTaxRatePercentage: string;
};

export type InvoiceTaxSettings = {
  membershipInvoices: InvoiceTaxCategorySettings;
  sponsorshipInvoices: InvoiceTaxCategorySettings;
  eventTicketsInvoices: InvoiceTaxCategorySettings;
  advertisingInvoices: InvoiceTaxCategorySettings;
};

export type MonthlyMembershipAdministrativeFeeSettings = {
  enabled: boolean;
  amount: string;
};

export type MembershipDiscountSettings = {
  enabled: boolean;
};

export type ChamberInvoiceCopySettings = {
  enabled: boolean;
  email: string;
};

export type GovernanceGroup = { id: string; name: string };
export type GovernanceSettings = {
  boards: GovernanceGroup[];
  committees: GovernanceGroup[];
  taskForces: GovernanceGroup[];
};

export type OneTimeOnboardingFeeSettings = {
  enabled: boolean;
  code: string;
  description: string;
  amount: string;
};

export type AccountAllocation = {
  type: ChamberMemberProAccountAllocationTypeEnum;
  label: string;
  accountNumber: string;
  accountName: string;
};

export type ChamberGroupInsuranceBillingDetails = {
  companyName: string;
  streetAddress: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  phoneNumber: string;
};

export type ChamberGroupInsurancePaymentSettings = {
  enabled: boolean;
  billingDetails: ChamberGroupInsuranceBillingDetails;
};

export type SenderDomainDnsRecord = {
  type: ChamberMemberProSenderDomainDnsRecordTypeEnum;
  name: string;
  value: string;
};

export type SenderDomainSettings = {
  status: ChamberMemberProSenderDomainStatusEnum;
  domain?: string | null;
  senderEmail?: string | null;
  dnsRecords: SenderDomainDnsRecord[];
  verificationStartedAtUtcMilli?: number | null;
  verifiedAtUtcMilli?: number | null;
  lastStatusCheckedAtUtcMilli?: number | null;
  failureReason?: string | null;
};

export type EmailAutomation = {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  subject: string;
  preview: string;
  category?: "membership" | "event";
};

export type EmailTemplate = Omit<EmailAutomation, "preview"> & {
  body: string;
  bodyFormat?: ChamberMemberProEmailBodyFormatEnum | null;
  toEmails?: string[] | null;
  ccEmails?: string[] | null;
  /** Design step 4 "Additional Automated (CC) Email", stored separately. */
  additionalCcEmails?: string[] | null;
  bccEmails?: string[] | null;
  senderEmail?: string | null;
  updatedAtUtcMilli?: number | null;
};

export type ProrationMonth = {
  month: string;
  signupPrice: string;
  monthlyAmount: string;
};

export type MembershipApplicationCommunicationOptions = {
  emailEnabled: boolean;
  phoneEnabled: boolean;
  textMessageEnabled: boolean;
};

export type MonthDaySettingsValue = {
  // Month label ("January") and day text, like the fixed renewal date.
  month: string;
  day: string;
};

export type MultipleBillingDatesSettings = {
  enabled: boolean;
  firstBillingDate: MonthDaySettingsValue;
  secondBillingDate: MonthDaySettingsValue;
};

export type InvoiceBillingContactSettings = {
  name: string;
  email: string;
  phone: string;
};

export type ChamberSettingsFormState = {
  governance: GovernanceSettings;
  setup: {
    fiscalYearStartMonth: string;
    fiscalYearStartDay: string;
    currency: string;
    timeZone: string;
    membershipRenewalFixedDateEnabled: boolean;
    membershipRenewalFixedDateMonth: string;
    membershipRenewalFixedDateDay: string;
    // D-CS1 Multiple Membership Billing Dates (month label and day text).
    // Optional: when absent, the save omits it and the backend keeps the saved value.
    multipleBillingDates?: MultipleBillingDatesSettings;
    // Read only (backend allowlist): the Multi-Billing Dates row shows only
    // when true (Moeen Oct 2 2026: Thunder Bay). Never sent on save.
    multipleBillingDatesAvailable?: boolean;
    membershipApplicationCommunicationOptions: MembershipApplicationCommunicationOptions;
  };
  membership: {
    renewalNoticeDays: string;
    autoRenewalNoticeDays: string;
    renewalReminderOneDays: string;
    renewalReminderTwoDays: string;
    enabledPaymentSchedules: EnabledPaymentSchedules;
    partTimeEmployeesPerFullTime: string;
    memberBenefits: MemberBenefit[];
    membershipModels: MembershipModel[];
    proratedPrices: Record<string, Record<string, string>>;
    // Design (11) Membership Period: staff-set months per signup month
    // ("Jan".."Dec"), shared by every membership. Absent = up to the next
    // billing date.
    proratedPeriodMonths?: Record<string, number>;
    globalToggleState: {
      prorate: boolean;
      autoRenewal: boolean;
      boardApproval: boolean;
      // Step 5 "Require Payment to be Paid in Order to be an Active Member"
      // (CMP Dashboard (22), Monday 13335776351).
      requirePayment: boolean;
      autoApproveRenewals: boolean;
    };
    paymentBeforeApprovalRule: PaymentBeforeApprovalRule;
    // Read only: the Step 5 payment option and Step 6 option 2 without
    // approval are offered to this chamber (Sand Bay until Oct 28 2026).
    membershipApprovalRulesAvailable?: boolean;
    // Editable "Membership Options" intro text (D:6125-6129). "" = not set.
    // Optional: when absent, the save omits it and the backend keeps the saved value.
    membershipOptionsIntro?: string;
  };
  paymentsAndTaxes: {
    eTransferEmail: string;
    taxJurisdictionId: string;
    taxRegistrationNumber: string;
    invoiceStartNumber: string;
    invoiceStartNumberLocked: boolean;
    cardProcessingFee: CardProcessingFeeSettings;
    manualPaymentMethods: PaymentMethod[];
    provincialChamberFee: ProvincialChamberFeeSettings;
    invoiceTaxSettings: InvoiceTaxSettings;
    monthlyMembershipAdministrativeFee: MonthlyMembershipAdministrativeFeeSettings;
    membershipDiscount: MembershipDiscountSettings;
    paidInvoiceReceiptCopy: ChamberInvoiceCopySettings;
    createdInvoiceCopy: ChamberInvoiceCopySettings;
    oneTimeOnboardingFee: OneTimeOnboardingFeeSettings;
    eventTicketCreditCardPaymentMode: ChamberMemberProEventTicketCreditCardPaymentModeEnum;
    accountAllocations: AccountAllocation[];
    chamberGroupInsurancePayment: ChamberGroupInsurancePaymentSettings;
    invoicePaymentTerms: ChamberMemberProInvoicePaymentTermsEnum;
    overdueInvoiceLateFeePercentage: string;
    // D-CS9: $ amount and toggle beside the %.
    overdueInvoiceLateFeeAmountInDollars: string;
    overdueInvoiceLateFeeEnabled: boolean;
    // Design (24) 8640-8655: Invoice Footer Notes toggle (Invoicing Options).
    invoiceFooterNotesEnabled: boolean;
    // D-CS2 Invoice Billing Contact Details (Invoicing Options).
    invoiceBillingContact: InvoiceBillingContactSettings;
    stripeConnect: StripeConnectSettings;
  };
  email: {
    senderEmail: string;
    replyToEmail: string;
    recipientRoutingMode: ChamberMemberProEmailRecipientRoutingModeEnum;
    enabledSenderEmails: string[];
    emailTemplates: EmailTemplate[];
    senderDomain: SenderDomainSettings;
  };
  updatedAtUtcMilli?: number | null;
};

export const defaultStripeConnectSettings: StripeConnectSettings = {
  mode: ChamberMemberProStripeConnectModeEnum.Live,
  status: ChamberMemberProStripeConnectStatusEnum.NotConnected,
  connectedAccountId: null,
  chargesEnabled: false,
  payoutsEnabled: false,
  detailsSubmitted: false,
  creditCardPaymentsEnabled: false,
  creditCardPaymentsReady: false,
  onboardingStartedAtUtcMilli: null,
  connectedAtUtcMilli: null,
  lastStatusCheckedAtUtcMilli: null,
};

export const defaultMultipleBillingDatesSettings = (): MultipleBillingDatesSettings => ({
  enabled: false,
  firstBillingDate: { month: "January", day: "1" },
  secondBillingDate: { month: "July", day: "1" },
});

export const defaultInvoiceBillingContactSettings = (): InvoiceBillingContactSettings => ({
  name: "",
  email: "",
  phone: "",
});

export const defaultMembershipApplicationCommunicationOptions: MembershipApplicationCommunicationOptions =
  {
    emailEnabled: true,
    phoneEnabled: true,
    textMessageEnabled: true,
  };

export const defaultCardProcessingFeeSettings: CardProcessingFeeSettings = {
  enabled: false,
  percentage: "0",
  fixedAmount: "$0.00",
};

export const defaultProvincialChamberFeeSettings: ProvincialChamberFeeSettings = {
  enabled: false,
  amount: "$0.00",
  showSeparateOnInvoice: false,
};

export const defaultInvoiceTaxCategorySettings = (): InvoiceTaxCategorySettings => ({
  enabled: false,
  firstTaxRateLabel: "",
  firstTaxRatePercentage: "0",
  secondTaxRateLabel: "",
  secondTaxRatePercentage: "0",
});

export const defaultInvoiceTaxSettings = (): InvoiceTaxSettings => ({
  membershipInvoices: defaultInvoiceTaxCategorySettings(),
  sponsorshipInvoices: defaultInvoiceTaxCategorySettings(),
  eventTicketsInvoices: defaultInvoiceTaxCategorySettings(),
  advertisingInvoices: defaultInvoiceTaxCategorySettings(),
});

export const defaultMonthlyMembershipAdministrativeFeeSettings: MonthlyMembershipAdministrativeFeeSettings = {
  enabled: false,
  amount: "$0.00",
};

export const defaultMembershipDiscountSettings: MembershipDiscountSettings = {
  enabled: true,
};

export const defaultChamberInvoiceCopySettings: ChamberInvoiceCopySettings = {
  enabled: false,
  email: "",
};

export const defaultOneTimeOnboardingFeeSettings: OneTimeOnboardingFeeSettings = {
  enabled: false,
  code: "",
  description: "One-Time Onboarding Fee",
  amount: "$0.00",
};

export const defaultEventTicketCreditCardPaymentMode =
  ChamberMemberProEventTicketCreditCardPaymentModeEnum.AllTickets;

export const accountAllocationRows: Pick<AccountAllocation, "type" | "label">[] = [
  {
    type: ChamberMemberProAccountAllocationTypeEnum.MembershipSales,
    label: "Membership Sales",
  },
  {
    type: ChamberMemberProAccountAllocationTypeEnum.EventTicketSales,
    label: "Event Ticket Sales",
  },
  {
    type: ChamberMemberProAccountAllocationTypeEnum.SponsorshipSales,
    label: "Sponsorship Sales",
  },
  {
    type: ChamberMemberProAccountAllocationTypeEnum.AdvertisingSales,
    label: "Advertising Sales",
  },
  {
    type: ChamberMemberProAccountAllocationTypeEnum.CustomSales,
    label: "Custom Sales",
  },
];

export const defaultAccountAllocations = (): AccountAllocation[] =>
  accountAllocationRows.map((row) => ({
    ...row,
    accountNumber: "",
    accountName: "",
  }));

// Moeen (Oct 5 2026): the Chambers of Commerce Group Insurance Plan is the
// default Chamber Group Insurance payer (same as the backend default).
export const defaultChamberGroupInsuranceBillingDetails: ChamberGroupInsuranceBillingDetails = {
  companyName: "Chambers of Commerce Group Insurance Plan",
  streetAddress: "1051 King Edward Street",
  addressLine2: "",
  city: "Winnipeg",
  province: "Manitoba",
  postalCode: "R3H 0R4",
  country: "Canada",
  contactName: "Chambers Plan Administration",
  contactTitle: "Plan Administrator",
  contactEmail: "chambers@johnstongroup.ca",
  phoneNumber: "1-800-665-3365",
};

export const defaultChamberGroupInsurancePaymentSettings: ChamberGroupInsurancePaymentSettings = {
  enabled: false,
  billingDetails: defaultChamberGroupInsuranceBillingDetails,
};

export const defaultSenderDomainSettings: SenderDomainSettings = {
  status: ChamberMemberProSenderDomainStatusEnum.NotConfigured,
  domain: null,
  senderEmail: null,
  dnsRecords: [],
  verificationStartedAtUtcMilli: null,
  verifiedAtUtcMilli: null,
  lastStatusCheckedAtUtcMilli: null,
  failureReason: null,
};

export const defaultProrationMonths: ProrationMonth[] = [
  { month: "Jan", signupPrice: "$1,200", monthlyAmount: "$100/mo" },
  { month: "Feb", signupPrice: "$1,100", monthlyAmount: "$100/mo" },
  { month: "Mar", signupPrice: "$1,000", monthlyAmount: "$100/mo" },
  { month: "Apr", signupPrice: "$900", monthlyAmount: "$100/mo" },
  { month: "May", signupPrice: "$800", monthlyAmount: "$100/mo" },
  { month: "Jun", signupPrice: "$700", monthlyAmount: "$100/mo" },
  { month: "Jul", signupPrice: "$600", monthlyAmount: "$100/mo" },
  { month: "Aug", signupPrice: "$500", monthlyAmount: "$100/mo" },
  { month: "Sep", signupPrice: "$400", monthlyAmount: "$100/mo" },
  { month: "Oct", signupPrice: "$300", monthlyAmount: "$100/mo" },
  { month: "Nov", signupPrice: "$200", monthlyAmount: "$100/mo" },
  { month: "Dec", signupPrice: "$100", monthlyAmount: "$100/mo" },
];

export const memberBenefits: MemberBenefit[] = [
  { id: "directory-listing", label: "Public member directory listing" },
  { id: "event-pricing", label: "Member pricing for chamber events" },
  { id: "newsletter", label: "Newsletter and advocacy updates" },
  { id: "business-referrals", label: "Business referrals from chamber staff" },
  { id: "job-board", label: "Job board posting access" },
  { id: "sponsorship-access", label: "Sponsorship and advertising opportunities" },
];

export const membershipAdditionalCosts: MembershipAdditionalCost[] = [
  {
    id: "feature-member-banner",
    code: "FMB",
    name: "Upgrade to Feature Member Banner",
    enabled: true,
    annualAmount: "$300.00",
    monthlyAmount: "$300.00",
    accountNumber: "4000",
    description:
      "Add up to 24 interchangeable image and video banners to your profile and perks on our website and Chamber Perks App™.",
  },
  {
    id: "advertising-category-banner",
    code: "ACB",
    name: "Advertising Category Banner Fee",
    enabled: true,
    annualAmount: "$300.00",
    monthlyAmount: "$300.00",
    accountNumber: "4000",
    description:
      "Add up to 4 interchangeable image and video banners to promote your company on one of our 16 category banners on our website and Chamber Perks App™.",
  },
];

export const membershipModels: MembershipModel[] = [
  {
    id: "company",
    code: "CM",
    name: "Company Membership",
    description: "For companies based on number of employees.",
    memberTarget: 0,
    enabled: true,
    publicMembershipApplicationEnabled: true,
    position: 1,
    paymentResponsibility: "Member/applicant pays",
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    pricingModel: "employeeRange",
    annualPrice: "$0.00",
    monthlyPrice: "$0.00",
    includedBenefitIds: [
      "directory-listing",
      "event-pricing",
      "newsletter",
      "business-referrals",
    ],
    additionalCosts: membershipAdditionalCosts,
    ranges: [
      {
        id: "company-1",
        code: "CM2-4",
        from: "2",
        to: "4",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-2",
        code: "CM5-10",
        from: "5",
        to: "10",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-3",
        code: "CM11-25",
        from: "11",
        to: "25",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-4",
        code: "CM26-50",
        from: "26",
        to: "50",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-5",
        code: "CM51-100",
        from: "51",
        to: "100",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-6",
        code: "CM101-200",
        from: "101",
        to: "200",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-7",
        code: "CM201-300",
        from: "201",
        to: "300",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-8",
        code: "CM301-500",
        from: "301",
        to: "500",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
      {
        id: "company-9",
        code: "CM501+",
        from: "501",
        to: "",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        memberTarget: 0,
      },
    ],
  },
  {
    id: "non-profit",
    code: "NFPM",
    name: "Not-For-Profit Membership",
    description: "For charities and non-profit organizations.",
    memberTarget: 0,
    enabled: true,
    publicMembershipApplicationEnabled: true,
    position: 2,
    paymentResponsibility: "Member/applicant pays",
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    pricingModel: "fixed",
    annualPrice: "$0.00",
    monthlyPrice: "$0.00",
    includedBenefitIds: ["directory-listing", "event-pricing", "newsletter"],
    additionalCosts: [],
    ranges: [],
  },
  {
    id: "individual-student",
    code: "SM",
    name: "Individual/Student Membership",
    description: "For individuals, entrepreneurs and students.",
    memberTarget: 0,
    enabled: false,
    publicMembershipApplicationEnabled: false,
    position: 3,
    paymentResponsibility: "Member/applicant pays",
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    pricingModel: "fixed",
    annualPrice: "$0.00",
    monthlyPrice: "$0.00",
    includedBenefitIds: ["event-pricing", "newsletter"],
    additionalCosts: [],
    ranges: [],
  },
  {
    id: "retired",
    code: "RM",
    name: "Retired Membership",
    description: "For retirees and former business owners.",
    memberTarget: 0,
    enabled: false,
    publicMembershipApplicationEnabled: false,
    position: 4,
    paymentResponsibility: "Member/applicant pays",
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    pricingModel: "fixed",
    annualPrice: "$0.00",
    monthlyPrice: "$0.00",
    includedBenefitIds: ["event-pricing", "newsletter"],
    additionalCosts: [],
    ranges: [],
  },
  {
    id: "associated-company",
    code: "ACM",
    name: "Associated Company Membership",
    description:
      "Associate Members must be affiliated with an active Primary Member company. The Associate company must have a minimum of 50% common ownership with the Primary Member company and must fall under the lower of the two membership fee categories.",
    memberTarget: 0,
    enabled: true,
    publicMembershipApplicationEnabled: true,
    position: 5,
    paymentResponsibility: "Member/applicant pays",
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    pricingModel: "fixed",
    annualPrice: "$0.00",
    monthlyPrice: "$0.00",
    includedBenefitIds: ["directory-listing", "event-pricing", "newsletter"],
    additionalCosts: [],
    ranges: [],
  },
];

export const paymentMethods: PaymentMethod[] = [
  {
    id: "credit-card",
    label: "Credit Card",
    description: "Online card payments through the chamber Stripe account.",
    enabled: false,
    emailSubject: "{chamberName} membership payment link for {memberName}",
    emailBody: `Hi {memberName},

Thank you for submitting your membership application to {chamberName}.

Your selected membership is {membershipType} and the amount due is {paymentAmount}. You can complete your payment securely by credit card using the link below:

{paymentLink}

This secure payment link will expire on {paymentLinkExpirationDate}. Once payment is received, you will receive a confirmation email and our team will continue processing your membership.

If you have questions about your application or payment, please contact us at {chamberEmail}.

Thank you,
{chamberName}`,
  },
  {
    id: ChamberMemberProManualPaymentMethodTypeEnum.Cheque,
    label: "Company Cheque",
    description: "Allow applicants and members to pay by company cheque.",
    enabled: true,
    emailSubject:
      "Company cheque payment instructions for {chamberName} membership",
    emailBody: `Hi {memberName},

Thank you for applying for {membershipType} with {chamberName}.

Your application total is {paymentAmount}. Please make your cheque payable to {chamberName}.

Invoice number: {invoiceNumber}

Once your cheque has been received and recorded, you will receive a payment confirmation email.

Thank you,
{chamberName}`,
  },
  {
    id: ChamberMemberProManualPaymentMethodTypeEnum.Cash,
    label: "Cash",
    description: "Manual cash collection tracked by chamber staff.",
    enabled: true,
    emailSubject: "Cash payment instructions for {chamberName} membership",
    emailBody: `Hi {memberName},

Thank you for applying for {membershipType} with {chamberName}.

Your application total is {paymentAmount}. To pay by cash, please visit our office during business hours:

{chamberOfficeAddress}

Office hours: {officeHours}

Please bring a copy of your invoice or reference number {invoiceNumber} so our team can apply the payment to the correct membership application.

Once your cash payment has been recorded, you will receive a payment confirmation email.

Thank you,
{chamberName}`,
  },
  {
    id: ChamberMemberProManualPaymentMethodTypeEnum.ETransfer,
    label: "E-transfer",
    description: "Manual e-transfer payment option.",
    enabled: true,
    emailSubject: "E-transfer instructions for {chamberName} membership",
    emailBody: `Hi {memberName},

Thank you for submitting your {membershipType} application.

Your amount due is {paymentAmount}. To pay by e-transfer, please send payment to:

{etransferEmail}

Please include invoice number {invoiceNumber} and member name {memberName} in the transfer message so we can match the payment to your account.

Security answer, if required: {etransferSecurityAnswer}

After your e-transfer is received and recorded, you will receive a confirmation email.

Thank you,
{chamberName}`,
  },
  {
    id: ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit,
    label: "Direct deposit",
    description: "Manual direct deposit payment option.",
    enabled: true,
    emailSubject: "Direct deposit instructions for invoice {invoiceNumber}",
    emailBody: `Hi {memberName},

Thank you for applying for {membershipType} with {chamberName}.

Your amount due is {paymentAmount}. To pay by direct deposit, please use the banking details below:

Bank name: {bankName}
Transit number: {transitNumber}
Institution number: {institutionNumber}
Account number: {accountNumber}

Please use invoice number {invoiceNumber} as the payment reference and email your deposit confirmation to {chamberEmail}.

Once our team confirms the deposit, your payment will be recorded and you will receive a receipt.

Thank you,
{chamberName}`,
  },
];

export const emailAutomations: EmailAutomation[] = [
  {
    id: "membership-confirmation",
    title: "Membership Application Confirmation (APPROVAL REQUIRED) Auto Email",
    description:
      "Sent to a prospective member when a membership application is submitted and has not been approved and payment not received",
    enabled: true,
    subject:
      "Thank you for submitting your membership application for {chamberName}",
    preview: `Hi {memberName},

Thank you for applying for {membershipType} with {chamberName}.

We have received your application and our team will review the details you submitted for {businessName}. If anything is missing, we will contact you at {memberEmail}.

Application reference: {applicationNumber}

You will receive another email once your application has been reviewed or when payment instructions are ready.

Thank you,
{chamberName}`,
  },
  {
    id: "payment-received",
    title:
      "Membership Application Approved (NO APPROVAL REQUIRED) and Payment Received Auto Email",
    description:
      "Sent to a prospective member after submitting their membership application  and payment ( not requiring approval)",
    enabled: true,
    subject: "Welcome to {chamberName} - Your Membership is Now Active",
    preview: `Hi {memberName},

Thank you. We have received your payment of {paymentAmount} for invoice {invoiceNumber}.

Payment method: {paymentMethod}
Payment date: {paymentDate}
Membership type: {membershipType}

Your receipt is available here:

{receiptLink}

Thank you for supporting {chamberName}.

{chamberName}`,
  },
  {
    id: "membership-approved",
    title: "Membership Application Status Update (Approved) Auto Email",
    description:
      "Sent after your chamber or Board of Directors has approved a membership application. (Used only when approval required has been enabled by your chamber)",
    enabled: true,
    subject: "Congratulations your {chamberName} membership has been approved",
    preview: `Hi {memberName},

Welcome to {chamberName}. Your {membershipType} membership for {businessName} has been approved.

Membership start date: {membershipStartDate}
Renewal date: {membershipRenewalDate}

You can access your member profile and update your listing here:

{memberPortalLink}

We are excited to have you as part of the chamber community.

Thank you,
{chamberName}`,
  },
  {
    id: "membership-declined",
    title: "Membership Application Status Update (Declined) Auto Email",
    description: "Sent to a prospective member when a membership application is declined.",
    enabled: true,
    subject: "Update on your {chamberName} membership application",
    preview: `Hi {memberName},

Thank you for applying for membership with {chamberName}.

After reviewing the application for {businessName}, we are unable to approve the membership at this time.

Reason or next step:
{declineReason}

If you believe this decision was made in error or would like to discuss the application, please contact us at {chamberEmail}.

Thank you,
{chamberName}`,
  },
  {
    id: "renewal-reminder",
    title: "Membership Renewal Reminder Auto Email",
    description: "Sent to member prior to membership renewal date.",
    enabled: true,
    subject:
      "Friendly reminder that your {chamberName} membership is up for renewal",
    preview: `Hi {memberName},

Your {membershipType} membership with {chamberName} is coming up for renewal.

Renewal date: {renewalDate}
Renewal amount: {renewalAmount}
Current member profile: {memberPortalLink}

Please review your membership details and make any needed updates before renewal.

Renew membership:
{renewalLink}

Thank you for continuing to support {chamberName}.

{chamberName}`,
  },
  {
    id: "membership-invoice-overdue-reminder",
    title: "Overdue Membership Invoice Reminder Auto Email",
    description:
      "Sent once when an unpaid membership invoice is 30 days past its due date.",
    enabled: true,
    subject: "Friendly reminder: invoice {invoiceNumber} is overdue",
    preview: `Hi {memberName},

This is a friendly reminder that membership invoice {invoiceNumber} for {businessName} has an outstanding balance of {amountDue}. It was due on {dueDate}.

A current PDF copy of the invoice is attached.

{paymentInstructions}

If you have already paid, please disregard this reminder. If you have questions, contact us at {chamberEmail}.

Thank you,
{chamberName}`,
  },
  {
    id: "event-registration-acknowledgement",
    title: "Event Registration Confirmation Auto Email",
    category: "event",
    description: "Sent after an event registration is submitted.",
    enabled: true,
    subject:
      "You're Registered for {EventName} - We Look Forward to Seeing You!",
    preview: `Hi {purchaserName},

Thank you for registering for {eventName} with {chamberName}.

Event date: {eventDate}
Ticket summary: {ticketSummary}
Registration reference: {registrationNumber}

If this event includes virtual access, the private event link will be shared only after registration is confirmed.

Thank you,
{chamberName}`,
  },
  {
    id: "event-invoice-me-payment-request",
    title: "Event Registration Submitted - Payment Required Request Auto Email",
    category: "event",
    description:
      "Sent when an individual has registered for an event and requested an invoice be sent for payment.",
    enabled: true,
    subject: "You're Registered for {EventName} -Please Submit Payment",
    preview: `Hi {purchaserName},

We received your registration for {eventName}. Please complete payment using the instructions below.

Amount due: {amountDue}
Invoice number: {invoiceNumber}
Payment instructions:
{paymentInstructions}

Your registration will remain tracked in Chamber Member Pro while payment is pending.

Thank you,
{chamberName}`,
  },
  {
    id: "event-payment-receipt",
    title: "Event Payment Receipt Auto Email",
    category: "event",
    description: "Sent after payment has been received",
    enabled: true,
    subject: "Thank you for Submitting your Payment for {EventName}",
    preview: `Hi {eventRecipientName},

Thank you. We received your payment of {paymentAmount} for {eventName}.

Payment method: {paymentMethod}
Payment date: {paymentDate}
Invoice number: {invoiceNumber}

Receipt link:
{receiptLink}

Thank you,
{chamberName}`,
  },
  {
    id: "event-sponsorship-request-received",
    title: "Sponsorship Request Received Auto Email",
    category: "event",
    description: "Sent to a member who has requested an event sponsorship",
    enabled: true,
    subject: "Thank you for your sponsorship request for {eventName}",
    preview: `Hi {sponsorContactName},

Thank you for requesting the {sponsorshipPackageName} sponsorship for {eventName}.

Our team will review the request before any sponsorship is shown publicly. If approved, we will send hosted payment or invoice instructions.

Thank you,
{chamberName}`,
  },
  {
    id: "event-sponsorship-approval-payment-request",
    title: "Sponsorship Approval/Payment Request Auto Email",
    category: "event",
    description:
      "Sent to a member to confirm their sponsorship request has been approved for {EventName}",
    enabled: true,
    subject:
      "Your sponsorship request for {eventName} has been approved – please submit payment",
    preview: `Hi {sponsorContactName},

Your {sponsorshipPackageName} sponsorship for {eventName} has been approved.

Amount due: {amountDue}
Payment link or instructions:
{paymentInstructions}

Your sponsorship will appear publicly after payment is confirmed.

Thank you,
{chamberName}`,
  },
  {
    id: "event-sponsorship-rejection",
    title: "Sponsorship Rejection Auto Email",
    category: "event",
    description:
      "Sent to a member when their sponsorship request can not be fulfilled",
    enabled: true,
    subject: "Update on your sponsorship request for {eventName}",
    preview: `Hi {sponsorContactName},

Thank you for your sponsorship request for {eventName}.

After review, we are unable to approve this request at this time.

Reason or next step:
{rejectionReason}

Thank you,
{chamberName}`,
  },
  {
    id: "event-24-hour-reminder",
    title: "24-Hour Event Reminder",
    category: "event",
    description:
      "Sent 24 hrs before event to all individuals registered for the event",
    enabled: true,
    subject:
      "Friendly Reminder you are registered for {eventName} tomorrow",
    preview: `Hi {attendeeName},

This is a reminder that {eventName} is coming up.

Date and time: {eventDateTime}
Location: {eventLocation}

If this event includes virtual access, use the private attendee link below:
{virtualEventLink}

Thank you,
{chamberName}`,
  },
];
